// Shared UI primitives — the PMS ui.js register adapted to --lms-*.
// Every surface uses these class strings so the design system stays one system.
import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { t } from '../lib/i18n';

// Interactive card = border + elevation + hover lift; static panel = no hover.
export const cardCls =
  'rounded-lg border border-lms-border bg-lms-surface shadow-[var(--lms-elev-1)] ' +
  'hover:border-[var(--lms-hover-border)] hover:shadow-[var(--lms-elev-2)]';
export const panelCls =
  'rounded-lg border border-lms-border bg-lms-surface shadow-[var(--lms-elev-1)]';

export const btnPrimary =
  'inline-flex items-center gap-1.5 rounded-md bg-lms-accent px-3 py-1.5 text-sm ' +
  'font-semibold text-lms-accent-ink disabled:opacity-60 hover:brightness-110';
export const btnGhost =
  'inline-flex items-center gap-1.5 rounded-md border border-lms-border px-3 py-1.5 ' +
  'text-sm text-lms-muted hover:text-lms-text hover:border-[var(--lms-hover-border)]';
export const btnDanger =
  'inline-flex items-center gap-1.5 rounded-md border border-lms-border px-3 py-1.5 ' +
  'text-sm text-[var(--lms-danger)] hover:border-[var(--lms-danger)]';

export const inputCls =
  'w-full rounded-md border border-lms-border bg-lms-input px-3 py-2 text-sm ' +
  'outline-none focus:border-[var(--lms-focus-border)]';
export const labelCls = 'block text-sm text-lms-muted mb-1.5';

const STATUS_STYLE = {
  draft: 'text-lms-muted border-lms-border',
  pending: 'text-[var(--lms-warning)] border-[var(--lms-warning)]',
  active: 'text-[var(--lms-success)] border-[var(--lms-success)]',
};

export function StatusBadge({ status }) {
  return (
    <span className={`inline-block rounded-full border px-2 py-0.5 text-xs ${STATUS_STYLE[status] || STATUS_STYLE.draft}`}>
      {t(status || 'draft')}
    </span>
  );
}

export function Field({ label, children, hint }) {
  return (
    <div>
      <span className={labelCls}>{label}</span>
      {children}
      {hint ? <p className="mt-1 text-xs text-lms-faint">{hint}</p> : null}
    </div>
  );
}

// Open modals, innermost last: Escape and the scroll lock belong to the top one
// only, so closing a stacked modal never closes (and loses) the one beneath.
const modalStack = [];

export function Modal({ title, onClose, children, wide = false }) {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const token = {};
    modalStack.push(token);
    const onKey = (e) => e.key === 'Escape' && modalStack[modalStack.length - 1] === token && closeRef.current();
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      modalStack.splice(modalStack.indexOf(token), 1);
      if (!modalStack.length) document.body.style.overflow = '';
    };
  }, []);
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/55 p-4 pt-[8vh]" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div role="dialog" aria-modal="true" className={`w-full ${wide === 'xl' ? 'max-w-3xl' : wide ? 'max-w-2xl' : 'max-w-md'} rounded-lg border border-lms-border bg-lms-surface shadow-[var(--lms-elev-3)]`}>
        <div className="flex items-center justify-between border-b border-lms-border px-5 py-3.5">
          <h2 className="text-sm font-semibold">{title}</h2>
          <button onClick={onClose} aria-label={t('Close')} className="text-lms-muted hover:text-lms-text">
            <X size={16} />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

export function EmptyState({ children }) {
  return (
    <div className="rounded-lg border border-dashed border-lms-border px-6 py-10 text-center text-sm text-lms-muted">
      {children}
    </div>
  );
}
